const JOURNEY_KEY_PREFIX = "onboarding_journey_";
const GUEST_SEEN_TOOLTIPS_KEY = "harmonauria_guest_seen_tooltips";

function normalizeSeenTooltips(value) {
  return Array.isArray(value) ? value.filter(Boolean) : [];
}

function readGuestSeenTooltips() {
  try {
    return normalizeSeenTooltips(JSON.parse(localStorage.getItem(GUEST_SEEN_TOOLTIPS_KEY) || "[]"));
  } catch {
    return [];
  }
}

function writeGuestSeenTooltips(value) {
  try {
    localStorage.setItem(GUEST_SEEN_TOOLTIPS_KEY, JSON.stringify(normalizeSeenTooltips(value)));
  } catch {}
}

function clearGuestSeenTooltips() {
  try {
    localStorage.removeItem(GUEST_SEEN_TOOLTIPS_KEY);
  } catch {}
}

export const tooltipManager = {
  _active: null,
  _queue: [],
  _drainTimer: 0,
  _listeners: new Set(),
  _seenListeners: new Set(),
  _activeTour: null,
  _tourListeners: new Set(),
  _userId: null,
  _authReady: false,
  _authReadyListeners: new Set(),
  _seenTooltips: new Set(),
  _optimisticSeen: new Set(),

  beginAuthSync() {
    this._authReady = false;
    this._userId = null;
    this._seenTooltips = new Set();
    this._optimisticSeen = new Set();
    this._resetRuntime();
    this._emitSeen();
  },

  setUser(uid, seenTooltips = []) {
    const nextUserId = uid || null;
    const userChanged = this._userId !== nextUserId;
    const remoteSeen = new Set(normalizeSeenTooltips(seenTooltips));
    const guestSeen = new Set(readGuestSeenTooltips());
    const nextSeen = nextUserId ? new Set([...remoteSeen, ...guestSeen]) : guestSeen;

    if (userChanged) this._resetRuntime();

    this._userId = nextUserId;
    this._seenTooltips = nextSeen;
    this._optimisticSeen = nextUserId ? new Set([...guestSeen].filter((id) => !remoteSeen.has(id))) : new Set();
    this._authReady = true;
    this._removeSeenEntries();

    this._authReadyListeners.forEach((callback) => {
      try {
        callback();
      } catch {}
    });
    this._authReadyListeners.clear();
    this._emitSeen();
    this._emit();
    this._scheduleDrain();

    if (nextUserId && guestSeen.size > 0) this._syncGuestSeenToUser(nextUserId, [...guestSeen]);
  },

  onAuthReady(callback) {
    if (this._authReady) {
      try {
        callback();
      } catch {}
      return () => {};
    }

    this._authReadyListeners.add(callback);
    return () => this._authReadyListeners.delete(callback);
  },

  isReady() {
    return this._authReady;
  },

  setSeenTooltips(value) {
    const remoteSeen = new Set(normalizeSeenTooltips(value));

    this._optimisticSeen.forEach((id) => {
      if (remoteSeen.has(id)) this._optimisticSeen.delete(id);
      else remoteSeen.add(id);
    });

    this._seenTooltips = remoteSeen;
    this._removeSeenEntries();
    this._emitSeen();
    this._emit();
    this._scheduleDrain();
  },

  hasSeenTooltip(id) {
    if (!this._authReady) return true;
    return this._seenTooltips.has(id);
  },

  hasSeenAll(ids = []) {
    if (!this._authReady) return false;
    return ids.every((id) => this._seenTooltips.has(id));
  },

  subscribeSeen(callback) {
    this._seenListeners.add(callback);

    try {
      callback(new Set(this._seenTooltips));
    } catch {}

    return () => this._seenListeners.delete(callback);
  },

  markTooltipAsSeen(id) {
    if (!id || this._seenTooltips.has(id)) return;

    this._seenTooltips.add(id);
    this._optimisticSeen.add(id);
    this._queue = this._queue.filter((entry) => entry.id !== id);
    this._emitSeen();

    if (!this._userId) {
      writeGuestSeenTooltips([...this._seenTooltips]);
      return;
    }

    const userId = this._userId;

    Promise.all([import("../firebase-config"), import("firebase/firestore")])
      .then(([{ db }, { arrayUnion, doc, updateDoc }]) => updateDoc(doc(db, "users", userId), { "guidesSeen.tooltips": arrayUnion(id) }))
      .catch((error) => console.warn("markTooltipAsSeen failed:", error));
  },

  setActiveTour(tourId) {
    if (this._activeTour === tourId) return;

    this._activeTour = tourId;

    if (this._active && !this._active.allowDuringTour) {
      this._upsertQueue({ ...this._active, requestedAt: Date.now() });
      this._active = null;
      this._emit();
    }

    this._tourListeners.forEach((callback) => {
      try {
        callback(tourId);
      } catch {}
    });

    this._scheduleDrain();
  },

  clearActiveTour(tourId) {
    if (tourId && this._activeTour !== tourId) return;

    this._activeTour = null;

    this._tourListeners.forEach((callback) => {
      try {
        callback(null);
      } catch {}
    });

    this._scheduleDrain(350);
  },

  isTourActive() {
    return this._activeTour !== null;
  },

  subscribeTour(callback) {
    this._tourListeners.add(callback);

    try {
      callback(this._activeTour);
    } catch {}

    return () => this._tourListeners.delete(callback);
  },

  _getUserKey() {
    return this._userId || "guest";
  },

  _getJourneyKey(uid = this._getUserKey()) {
    return `${JOURNEY_KEY_PREFIX}${uid || "guest"}`;
  },

  getJourneyStep() {
    if (!this._authReady) return null;

    try {
      return localStorage.getItem(this._getJourneyKey()) || "home_tour";
    } catch {
      return "home_tour";
    }
  },

  setJourneyStep(step) {
    try {
      localStorage.setItem(this._getJourneyKey(), step);
    } catch {}
  },

  isJourneyDone() {
    return this.getJourneyStep() === "done";
  },

  subscribe(callback) {
    this._listeners.add(callback);

    try {
      callback(this._active?.id || null);
    } catch {}

    return () => this._listeners.delete(callback);
  },

  requestShow(id, priority = 0, options = {}) {
    if (!id) return false;
    if (this._authReady && this._seenTooltips.has(id)) return false;

    const entry = {
      id,
      priority: Number(priority) || 0,
      allowDuringTour: options.allowDuringTour === true,
      sequence: options.sequence || "",
      order: Number(options.order) || 0,
      requestedAt: Date.now(),
    };

    if (this._active?.id === id) {
      this._active = { ...this._active, ...entry };
      this._emit();
      return true;
    }

    this._upsertQueue(entry);
    if (this._authReady) this._scheduleDrain();
    return false;
  },

  dismiss(id) {
    if (this._active?.id !== id) {
      this._queue = this._queue.filter((entry) => entry.id !== id);
      return;
    }

    this._active = null;
    this._emit();
    this._scheduleDrain(320);
  },

  release(id) {
    if (!id) return;

    let changed = false;

    if (this._active?.id === id) {
      this._active = null;
      changed = true;
    }

    const nextQueue = this._queue.filter((entry) => entry.id !== id);
    if (nextQueue.length !== this._queue.length) changed = true;
    this._queue = nextQueue;

    if (changed) this._emit();
    this._scheduleDrain();
  },

  resetAllTooltips(uid) {
    const userKey = uid || this._getUserKey();

    try {
      localStorage.removeItem(this._getJourneyKey(userKey));
      localStorage.removeItem(this._getJourneyKey("guest"));
    } catch {
      console.warn("Impossible de réinitialiser les tooltips");
    }

    clearGuestSeenTooltips();
    this._seenTooltips = new Set();
    this._optimisticSeen = new Set();
    this._resetRuntime();
    this._emitSeen();
  },

  resetFullGuidedExperience(uid) {
    const userKey = uid || this._getUserKey();

    this.resetAllTooltips(userKey);

    try {
      localStorage.removeItem(`guided_tour_v3_${userKey}`);
      localStorage.removeItem(`player_tour_natural_v3_${userKey}`);
      localStorage.removeItem("guided_tour_v3_guest");
      localStorage.removeItem("player_tour_natural_v3_guest");
      localStorage.setItem(this._getJourneyKey(userKey), "home_tour");
    } catch {
      console.warn("Impossible de réinitialiser la visite guidée");
    }

    this._activeTour = null;
    this._tourListeners.forEach((callback) => {
      try {
        callback(null);
      } catch {}
    });
  },

  _syncGuestSeenToUser(userId, ids) {
    if (!userId || ids.length === 0) return;

    Promise.all([import("../firebase-config"), import("firebase/firestore")])
      .then(([{ db }, { arrayUnion, doc, updateDoc }]) => updateDoc(doc(db, "users", userId), { "guidesSeen.tooltips": arrayUnion(...ids) }))
      .then(() => clearGuestSeenTooltips())
      .catch((error) => console.warn("guest tooltip handoff failed:", error));
  },

  _upsertQueue(entry) {
    this._queue = this._queue.filter((queuedEntry) => queuedEntry.id !== entry.id);
    this._queue.push(entry);
  },

  _scheduleDrain(delay = 0) {
    if (this._drainTimer) return;

    this._drainTimer = window.setTimeout(() => {
      this._drainTimer = 0;
      this._drainQueue();
    }, delay);
  },

  _drainQueue() {
    if (!this._authReady || this._active || this._queue.length === 0) return;

    const candidates = this._activeTour ? this._queue.filter((entry) => entry.allowDuringTour) : [...this._queue];
    if (candidates.length === 0) return;

    candidates.sort((left, right) => {
      if (left.sequence && left.sequence === right.sequence && left.order !== right.order) return left.order - right.order;
      if (left.priority !== right.priority) return right.priority - left.priority;
      return left.requestedAt - right.requestedAt;
    });

    const next = candidates[0];
    this._queue = this._queue.filter((entry) => entry.id !== next.id);
    this._active = next;
    this._emit();
  },

  _removeSeenEntries() {
    if (this._active && this._seenTooltips.has(this._active.id)) this._active = null;
    this._queue = this._queue.filter((entry) => !this._seenTooltips.has(entry.id));
  },

  _resetRuntime() {
    if (this._drainTimer) window.clearTimeout(this._drainTimer);
    this._drainTimer = 0;
    this._active = null;
    this._queue = [];
    this._emit();
  },

  _emit() {
    const activeId = this._active?.id || null;
    this._listeners.forEach((callback) => {
      try {
        callback(activeId);
      } catch {}
    });
  },

  _emitSeen() {
    const snapshot = new Set(this._seenTooltips);
    this._seenListeners.forEach((callback) => {
      try {
        callback(snapshot);
      } catch {}
    });
  },
};
